"use client"

import { useState } from "react"
import { Star, ShieldCheck, Pencil, Trash2, BadgeAlert, Loader2 } from "lucide-react"
import ReviewModal, { type ReviewData } from "./ReviewModal"

interface Props {
  review: ReviewData
  currentUserId?: string
  subjectName: string
  onUpdated: (r: ReviewData) => void
  onDeleted: (id: string) => void
}

function StarRow({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className="w-4 h-4"
          fill={rating >= s ? "#F59E0B" : "none"}
          stroke={rating >= s ? "#F59E0B" : "#D1D5DB"}
        />
      ))}
    </div>
  )
}

export default function ReviewCard({ review, currentUserId, subjectName, onUpdated, onDeleted }: Props) {
  const [editing, setEditing] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [loadingVerify, setLoadingVerify] = useState(false)
  const [deleteMsg, setDeleteMsg] = useState("")

  const isAuthor = currentUserId === review.author.id
  const isSubject = currentUserId !== review.author.id && currentUserId !== undefined

  async function handleDelete() {
    if (!confirm("Delete this review?")) return
    setLoadingDelete(true)
    const res = await fetch(`/api/reviews/${review.id}`, { method: "DELETE" })
    setLoadingDelete(false)
    if (res.ok) onDeleted(review.id)
    else {
      const d = await res.json()
      alert(d.error || "Failed to delete")
    }
  }

  async function handleDeleteRequest() {
    if (!confirm("Request deletion of this verified review? The other party must also agree for it to be removed.")) return
    setLoadingDelete(true)
    const res = await fetch(`/api/reviews/${review.id}/delete-request`, { method: "POST" })
    const d = await res.json()
    setLoadingDelete(false)
    if (d.deleted) onDeleted(review.id)
    else setDeleteMsg(d.message || "Deletion request recorded.")
  }

  async function handleVerify() {
    setLoadingVerify(true)
    const res = await fetch(`/api/reviews/${review.id}/verify`, { method: "POST" })
    const d = await res.json()
    setLoadingVerify(false)
    if (d.url) window.location.href = d.url
    else alert(d.error || "Something went wrong")
  }

  const dateStr = new Date(review.createdAt).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
  })

  return (
    <>
      <div className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            {/* Avatar */}
            <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-sm flex-shrink-0">
              {review.author.name.charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{review.author.name}</p>
              <p className="text-xs text-gray-400 capitalize">
                {review.author.userType}
                {review.author.location ? ` · ${review.author.location}` : ""}
              </p>
            </div>
          </div>

          <div className="flex-shrink-0 text-right space-y-1">
            <StarRow rating={review.rating} />
            <p className="text-xs text-gray-400">{dateStr}</p>
          </div>
        </div>

        {/* Verified badge */}
        {review.verified && (
          <div className="mt-3 flex items-center gap-1.5 text-emerald-600">
            <ShieldCheck className="w-4 h-4" />
            <span className="text-xs font-semibold">Verified</span>
          </div>
        )}

        {/* Review content */}
        <p className="mt-3 text-sm text-gray-700 leading-relaxed">{review.content}</p>

        {/* Delete pending note */}
        {deleteMsg && (
          <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <BadgeAlert className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800">{deleteMsg}</p>
          </div>
        )}

        {/* Action row */}
        <div className="mt-3 flex flex-wrap gap-2">
          {/* Edit — author only */}
          {isAuthor && (
            <button
              onClick={() => setEditing(true)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-blue-600 px-2 py-1 rounded-md hover:bg-gray-50"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
          )}

          {/* Delete — author on unverified; both parties on verified */}
          {(isAuthor || isSubject) && (
            <button
              onClick={review.verified ? handleDeleteRequest : handleDelete}
              disabled={loadingDelete}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 px-2 py-1 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              {loadingDelete ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Trash2 className="w-3.5 h-3.5" />
              )}
              {review.verified ? "Request Removal" : "Delete"}
            </button>
          )}

          {/* Verify — subject only, on unverified reviews */}
          {isSubject && !review.verified && (
            <button
              onClick={handleVerify}
              disabled={loadingVerify}
              className="flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2 py-1 rounded-md disabled:opacity-50 ml-auto"
            >
              {loadingVerify ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <ShieldCheck className="w-3.5 h-3.5" />
              )}
              Verify for $10
            </button>
          )}
        </div>
      </div>

      {editing && (
        <ReviewModal
          subjectId={review.author.id /* not used for edit */}
          subjectName={subjectName}
          existingReview={{ id: review.id, rating: review.rating, content: review.content }}
          onClose={() => setEditing(false)}
          onSaved={(updated) => { onUpdated(updated); setEditing(false) }}
        />
      )}
    </>
  )
}
